import React, { useState, useEffect } from 'react';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Alert,
  Box,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Typography,
  Paper,
  Chip,
  Fade,
  Collapse,
  useTheme as useMuiTheme,
  alpha
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Storage as StorageIcon,
  CalendarMonth as CalendarIcon,
  Close as CloseIcon,
  Save as SaveIcon,
  CloudDone as CloudDoneIcon,
  CloudOff as CloudOffIcon,
  Info as InfoIcon,
  DataObject as DataObjectIcon,
  DateRange as DateRangeIcon,
  Notes as NotesIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { FiscalPeriodDto, CreateFiscalPeriodDto } from '../../types/fiscalPeriods.types';
import { fiscalPeriodsService } from '../../services/fiscalPeriods.service';
import axiosInstance from '../../services/axiosInstance';

interface FiscalPeriodDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: CreateFiscalPeriodDto) => Promise<void>;
  period?: FiscalPeriodDto | null;
}

const FiscalPeriodDialog: React.FC<FiscalPeriodDialogProps> = ({
  open,
  onClose,
  onSave,
  period
}) => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const muiTheme = useMuiTheme();
  const isRTL = language === 'ar';
  const isEdit = !!period;
  const isDark = mode === 'dark';

  // Dynamic colors based on theme
  const colors = {
    primary: isDark ? '#90caf9' : '#667eea',
    secondary: isDark ? '#ce93d8' : '#764ba2',
    headerBg: isDark 
      ? 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)' 
      : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    cardBg: isDark ? alpha('#90caf9', 0.08) : alpha('#667eea', 0.05),
    cardBg2: isDark ? alpha('#ce93d8', 0.08) : alpha('#764ba2', 0.05),
    footerBg: isDark ? '#1e1e1e' : '#f5f5f5',
    inputBg: isDark ? '#2d2d2d' : '#fafafa',
    textPrimary: isDark ? '#ffffff' : '#1a1a1a',
    textSecondary: isDark ? '#b0b0b0' : '#666666',
    border: isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.12)',
    success: isDark ? '#66bb6a' : '#4caf50',
    successBg: isDark ? alpha('#66bb6a', 0.15) : alpha('#4caf50', 0.1),
  };

  const [formData, setFormData] = useState<CreateFiscalPeriodDto>({
    periodName: '',
    periodType: 'Yearly',
    startDate: '',
    endDate: '',
    isCurrent: false,
    useSeparateDatabase: false,
    databaseName: '',
    connectionString: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [showConnectionString, setShowConnectionString] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [generatingDb, setGeneratingDb] = useState(false);
  const [sourceFiscalPeriodId, setSourceFiscalPeriodId] = useState<number | null>(null);
  const [availableSourcePeriods, setAvailableSourcePeriods] = useState<FiscalPeriodDto[]>([]);

  useEffect(() => {
    if (period) {
      setFormData({
        periodName: period.periodName,
        periodType: period.periodType,
        startDate: period.startDate.split('T')[0],
        endDate: period.endDate.split('T')[0],
        isCurrent: period.isCurrent,
        useSeparateDatabase: period.useSeparateDatabase,
        databaseName: period.databaseName || '',
        connectionString: period.connectionString || '',
        notes: period.notes || ''
      });
    } else {
      setFormData({
        periodName: '',
        periodType: 'Yearly',
        startDate: '',
        endDate: '',
        isCurrent: false,
        useSeparateDatabase: false,
        databaseName: '',
        connectionString: '',
        notes: ''
      });
    }
    setError('');
    setConnectionStatus('idle');
    setConnectionMessage('');
  }, [period, open]);

  // ✅ تحميل السنوات المتاحة عند فتح Dialog
  useEffect(() => {
    const loadSourcePeriods = async () => {
      if (open && formData.useSeparateDatabase) {
        try {
          const periods = await fiscalPeriodsService.getAll();
          setAvailableSourcePeriods(periods);
          // اختيار السنة الحالية تلقائياً
          const current = periods.find(p => p.isCurrent);
          if (current) {
            setSourceFiscalPeriodId(current.fiscalPeriodId);
          }
        } catch (err) {
          console.error('Failed to load fiscal periods:', err);
        }
      }
    };
    loadSourcePeriods();
  }, [open, formData.useSeparateDatabase]);

  const handleChange = (field: keyof CreateFiscalPeriodDto, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (field === 'connectionString') {
      setConnectionStatus('idle');
      setConnectionMessage('');
    }
  };

  const handleTestConnection = async () => {
    if (!formData.connectionString?.trim()) {
      setConnectionStatus('error');
      setConnectionMessage(isRTL ? 'أدخل سلسلة الاتصال أولاً' : 'Enter connection string first');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus('idle');
    setConnectionMessage('');

    try {
      const response = await axiosInstance.post('/FiscalPeriods/test-connection', {
        connectionString: formData.connectionString
      });

      if (response.data.success) {
        setConnectionStatus('success');
        setConnectionMessage(isRTL ? 'تم الاتصال بنجاح!' : 'Connection successful!');
      } else {
        setConnectionStatus('error');
        setConnectionMessage(response.data.message || (isRTL ? 'فشل الاتصال' : 'Connection failed'));
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setConnectionMessage(err.response?.data?.message || (isRTL ? 'فشل اختبار الاتصال' : 'Connection test failed'));
    } finally {
      setTestingConnection(false);
    }
  };

  const handleGenerateDatabase = async () => {
    if (!formData.databaseName?.trim()) {
      setError(isRTL ? 'أدخل اسم قاعدة البيانات أولاً' : 'Enter database name first');
      return;
    }

    if (!sourceFiscalPeriodId) {
      setError(isRTL ? 'اختر السنة المالية المصدر' : 'Select source fiscal year');
      return;
    }

    setGeneratingDb(true);
    setError('');

    try {
      const result = await fiscalPeriodsService.generateDatabase(
        formData.databaseName,
        sourceFiscalPeriodId
      );

      if (result.success && result.connectionString) {
        setFormData(prev => ({ ...prev, connectionString: result.connectionString! }));
        setConnectionStatus('success');
        setConnectionMessage(isRTL ? 'تم إنشاء قاعدة البيانات بنجاح!' : 'Database created successfully!');
      } else {
        setError(result.message);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || (isRTL ? 'فشل إنشاء قاعدة البيانات' : 'Failed to create database'));
    } finally {
      setGeneratingDb(false);
    }
  };

  const handleSave = async () => {
    // ✅ إذا تم إنشاء قاعدة بيانات منفصلة - السجل موجود بالفعل!
    if (formData.useSeparateDatabase && formData.connectionString && !isEdit) {
      // إغلاق الـ Dialog مباشرة وتحديث القائمة
      onClose();
      setTimeout(() => {
        window.location.reload();
      }, 500);
      return;
    }

    if (!formData.periodName.trim()) {
      setError(isRTL ? 'اسم الفترة مطلوب' : 'Period name is required');
      return;
    }
    if (!formData.startDate) {
      setError(isRTL ? 'تاريخ البداية مطلوب' : 'Start date is required');
      return;
    }
    if (!formData.endDate) {
      setError(isRTL ? 'تاريخ النهاية مطلوب' : 'End date is required');
      return;
    }

    if (formData.useSeparateDatabase && !formData.connectionString?.trim()) {
      setError(isRTL ? 'سلسلة الاتصال مطلوبة عند استخدام قاعدة بيانات منفصلة' : 'Connection string is required when using separate database');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save period');
    } finally {
      setLoading(false);
    }
  };

  const periodTypeColors: Record<string, string> = {
    'Yearly': isDark ? '#66bb6a' : '#4caf50',
    'Quarterly': isDark ? '#ffb74d' : '#ff9800',
    'Monthly': isDark ? '#64b5f6' : '#2196f3'
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth 
      dir={isRTL ? 'rtl' : 'ltr'}
      TransitionComponent={Fade}
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: isDark ? '#121212' : '#ffffff',
          boxShadow: isDark 
            ? '0 24px 48px rgba(0, 0, 0, 0.5)' 
            : '0 24px 48px rgba(102, 126, 234, 0.25)'
        }
      }}
    >
      {/* Header */}
      <DialogTitle sx={{ 
        background: colors.headerBg, 
        color: 'white',
        p: 0
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          p: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ 
              bgcolor: 'rgba(255,255,255,0.2)', 
              borderRadius: 2, 
              p: 1, 
              display: 'flex',
              backdropFilter: 'blur(10px)'
            }}>
              <CalendarIcon sx={{ fontSize: 28 }} />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                {isEdit
                  ? (isRTL ? 'تعديل الفترة المالية' : 'Edit Fiscal Period')
                  : (isRTL ? 'إضافة فترة مالية جديدة' : 'Add New Fiscal Period')
                }
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {isRTL ? 'إدارة الفترات المالية وقواعد البيانات' : 'Manage fiscal periods and databases'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: isDark ? '#1a1a1a' : '#fafafa' }}>
        {error && (
          <Fade in>
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          </Fade>
        )}

        {/* Basic Information Section */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          bgcolor: colors.cardBg,
          border: `1px solid ${colors.border}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
            <DateRangeIcon sx={{ color: colors.primary }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: colors.primary }}>
              {isRTL ? 'المعلومات الأساسية' : 'Basic Information'}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <TextField
              fullWidth
              label={isRTL ? 'اسم الفترة' : 'Period Name'}
              value={formData.periodName}
              onChange={(e) => handleChange('periodName', e.target.value)}
              required
              disabled={period?.isClosed}
              placeholder={isRTL ? 'مثال: السنة المالية 2024' : 'e.g., Fiscal Year 2024'}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: isDark ? '#2d2d2d' : '#ffffff',
                  '&:hover fieldset': { borderColor: colors.primary },
                  '&.Mui-focused fieldset': { borderColor: colors.primary }
                },
                '& .MuiInputLabel-root.Mui-focused': { color: colors.primary }
              }}
            />

            <FormControl fullWidth required disabled={period?.isClosed}>
              <InputLabel sx={{ '&.Mui-focused': { color: colors.primary } }}>
                {isRTL ? 'نوع الفترة' : 'Period Type'}
              </InputLabel>
              <Select
                value={formData.periodType}
                onChange={(e) => handleChange('periodType', e.target.value)}
                label={isRTL ? 'نوع الفترة' : 'Period Type'}
                sx={{ 
                  borderRadius: 2,
                  bgcolor: isDark ? '#2d2d2d' : '#ffffff',
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary }
                }}
              >
                {['Yearly', 'Quarterly', 'Monthly'].map(type => (
                  <MenuItem key={type} value={type}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ 
                        width: 12, 
                        height: 12, 
                        borderRadius: '50%', 
                        bgcolor: periodTypeColors[type] 
                      }} />
                      {type === 'Yearly' ? (isRTL ? 'سنوية' : 'Yearly') :
                       type === 'Quarterly' ? (isRTL ? 'ربع سنوية' : 'Quarterly') :
                       (isRTL ? 'شهرية' : 'Monthly')}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={isRTL ? 'تاريخ البداية' : 'Start Date'}
                  value={formData.startDate ? new Date(formData.startDate) : null}
                  onChange={(date) => handleChange('startDate', date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                  format="dd/MM/yyyy"
                  views={['year', 'month', 'day']}
                  openTo="day"
                  disabled={period?.isClosed}
                  slotProps={{ textField: { fullWidth: true, required: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } } } }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label={isRTL ? 'تاريخ النهاية' : 'End Date'}
                  value={formData.endDate ? new Date(formData.endDate) : null}
                  onChange={(date) => handleChange('endDate', date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '')}
                  format="dd/MM/yyyy"
                  views={['year', 'month', 'day']}
                  openTo="day"
                  disabled={period?.isClosed}
                  slotProps={{ textField: { fullWidth: true, required: true, sx: { '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: isDark ? '#2d2d2d' : '#ffffff' } } } }}
                />
              </LocalizationProvider>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              bgcolor: formData.isCurrent ? colors.successBg : (isDark ? '#2d2d2d' : '#f5f5f5'),
              borderRadius: 2,
              p: 2,
              border: '1px solid',
              borderColor: formData.isCurrent ? colors.success : colors.border,
              transition: 'all 0.3s'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {formData.isCurrent ? <CheckCircleIcon sx={{ color: colors.success }} /> : <CalendarIcon color="action" />}
                <Typography fontWeight="medium">
                  {isRTL ? 'تعيين كفترة حالية' : 'Set as Current Period'}
                </Typography>
              </Box>
              <Switch
                checked={formData.isCurrent || false}
                onChange={(e) => handleChange('isCurrent', e.target.checked)}
                disabled={period?.isClosed}
                color="success"
              />
            </Box>
          </Box>
        </Paper>

        {/* Database Section */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3, 
          bgcolor: colors.cardBg2,
          border: `1px solid ${colors.border}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StorageIcon sx={{ color: colors.secondary }} />
              <Typography variant="subtitle1" fontWeight="bold" sx={{ color: colors.secondary }}>
                {isRTL ? 'قاعدة البيانات المنفصلة' : 'Separate Database'}
              </Typography>
              <Chip 
                label={isRTL ? 'اختياري' : 'Optional'} 
                size="small" 
                sx={{ 
                  bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)', 
                  fontSize: '0.7rem',
                  color: isDark ? '#b0b0b0' : '#666666'
                }} 
              />
            </Box>
            <Switch
              checked={formData.useSeparateDatabase || false}
              onChange={(e) => handleChange('useSeparateDatabase', e.target.checked)}
              disabled={period?.isClosed}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: colors.secondary },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: colors.secondary }
              }}
            />
          </Box>

          <Collapse in={formData.useSeparateDatabase}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
              <TextField
                fullWidth
                label={isRTL ? 'اسم قاعدة البيانات' : 'Database Name'}
                value={formData.databaseName}
                onChange={(e) => handleChange('databaseName', e.target.value)}
                disabled={period?.isClosed}
                placeholder={isRTL ? 'مثال: CountoraDB_2024' : 'e.g., CountoraDB_2024'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <DataObjectIcon sx={{ color: colors.textSecondary }} />
                    </InputAdornment>
                  )
                }}
                sx={{ 
                  '& .MuiOutlinedInput-root': { 
                    borderRadius: 2,
                    bgcolor: isDark ? '#2d2d2d' : '#ffffff'
                  } 
                }}
              />

              {/* ✅ Dropdown لاختيار السنة المصدر */}
              <FormControl fullWidth>
                <InputLabel sx={{ '&.Mui-focused': { color: colors.secondary } }}>
                  {isRTL ? 'نسخ من السنة المالية' : 'Copy from Fiscal Year'}
                </InputLabel>
                <Select
                  value={sourceFiscalPeriodId || ''}
                  onChange={(e) => setSourceFiscalPeriodId(Number(e.target.value))}
                  label={isRTL ? 'نسخ من السنة المالية' : 'Copy from Fiscal Year'}
                  disabled={period?.isClosed}
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: isDark ? '#2d2d2d' : '#ffffff',
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.secondary }
                  }}
                >
                  {availableSourcePeriods.map(p => (
                    <MenuItem key={p.fiscalPeriodId} value={p.fiscalPeriodId}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography sx={{ flex: 1 }}>
                          {p.periodName}
                        </Typography>
                        {p.useSeparateDatabase && (
                          <Chip 
                            label={p.databaseName || (isRTL ? 'قاعدة منفصلة' : 'Separate DB')} 
                            size="small" 
                            color="warning" 
                          />
                        )}
                        {p.isCurrent && (
                          <Chip 
                            label={isRTL ? 'حالية' : 'Current'} 
                            size="small" 
                            color="success" 
                          />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* ✅ زر Generate Database */}
              <Button
                variant="contained"
                onClick={handleGenerateDatabase}
                disabled={generatingDb || period?.isClosed || !formData.databaseName?.trim()}
                startIcon={generatingDb ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon />}
                sx={{ 
                  alignSelf: 'flex-start',
                  borderRadius: 2,
                  px: 3,
                  background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                  boxShadow: '0 4px 14px rgba(17, 153, 142, 0.4)',
                  '&:hover': { 
                    background: 'linear-gradient(135deg, #0f8678 0%, #30d66a 100%)',
                    boxShadow: '0 6px 20px rgba(17, 153, 142, 0.5)'
                  }
                }}
              >
                {generatingDb 
                  ? (isRTL ? 'جاري الإنشاء...' : 'Creating...') 
                  : (isRTL ? 'إنشاء قاعدة بيانات جديدة' : 'Generate New Database')
                }
              </Button>

              <Box sx={{ position: 'relative' }}>
                <TextField
                  fullWidth
                  label={isRTL ? 'سلسلة الاتصال (Connection String)' : 'Connection String'}
                  value={formData.connectionString}
                  onChange={(e) => handleChange('connectionString', e.target.value)}
                  disabled={period?.isClosed}
                  type={showConnectionString ? 'text' : 'password'}
                  multiline={showConnectionString}
                  rows={showConnectionString ? 3 : 1}
                  placeholder="Server=...;Database=...;User Id=...;Password=...;TrustServerCertificate=True;"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={isRTL ? (showConnectionString ? 'إخفاء' : 'إظهار') : (showConnectionString ? 'Hide' : 'Show')}>
                          <IconButton
                            onClick={() => setShowConnectionString(!showConnectionString)}
                            edge="end"
                            size="small"
                          >
                            {showConnectionString ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-root': { 
                      borderRadius: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.85rem',
                      bgcolor: isDark ? '#2d2d2d' : '#ffffff'
                    } 
                  }}
                />
              </Box>

              {/* Connection Status */}
              {connectionStatus !== 'idle' && (
                <Fade in>
                  <Alert 
                    severity={connectionStatus === 'success' ? 'success' : 'error'}
                    icon={connectionStatus === 'success' ? <CloudDoneIcon /> : <CloudOffIcon />}
                    sx={{ borderRadius: 2 }}
                  >
                    {connectionMessage}
                  </Alert>
                </Fade>
              )}

              {/* Test Connection Button */}
              <Button
                variant="outlined"
                onClick={handleTestConnection}
                disabled={testingConnection || period?.isClosed || !formData.connectionString?.trim()}
                startIcon={testingConnection ? <CircularProgress size={18} /> : <StorageIcon />}
                sx={{ 
                  alignSelf: 'flex-start',
                  borderRadius: 2,
                  px: 3,
                  borderColor: colors.secondary,
                  color: colors.secondary,
                  '&:hover': { 
                    borderColor: colors.secondary, 
                    bgcolor: alpha(colors.secondary, 0.1)
                  }
                }}
              >
                {testingConnection 
                  ? (isRTL ? 'جاري الاختبار...' : 'Testing...') 
                  : (isRTL ? 'اختبار الاتصال' : 'Test Connection')
                }
              </Button>

              {/* Info Alert */}
              <Alert 
                severity="info" 
                icon={<InfoIcon />}
                sx={{ 
                  borderRadius: 2, 
                  bgcolor: isDark ? alpha('#2196f3', 0.1) : alpha('#2196f3', 0.08),
                  '& .MuiAlert-icon': { color: isDark ? '#64b5f6' : '#1976d2' },
                  '& .MuiAlert-message': { color: isDark ? '#b0b0b0' : 'inherit' }
                }}
              >
                {isRTL 
                  ? 'سيتم تشفير سلسلة الاتصال قبل حفظها في قاعدة البيانات للحفاظ على الأمان.'
                  : 'The connection string will be encrypted before saving to the database for security.'
                }
              </Alert>
            </Box>
          </Collapse>
        </Paper>

        {/* Notes Section */}
        <Paper elevation={0} sx={{ 
          p: 3, 
          borderRadius: 3, 
          bgcolor: isDark ? '#1e1e1e' : '#ffffff',
          border: `1px solid ${colors.border}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <NotesIcon sx={{ color: colors.primary }} />
            <Typography variant="subtitle1" fontWeight="bold" sx={{ color: colors.primary }}>
              {isRTL ? 'ملاحظات' : 'Notes'}
            </Typography>
          </Box>
          <TextField
            fullWidth
            value={formData.notes}
            onChange={(e) => handleChange('notes', e.target.value)}
            multiline
            rows={3}
            disabled={period?.isClosed}
            placeholder={isRTL ? 'أضف ملاحظات إضافية هنا...' : 'Add additional notes here...'}
            sx={{ 
              '& .MuiOutlinedInput-root': { 
                borderRadius: 2,
                bgcolor: isDark ? '#2d2d2d' : '#f5f5f5'
              } 
            }}
          />
        </Paper>
      </DialogContent>

      {/* Footer */}
      <DialogActions sx={{ 
        p: 3, 
        bgcolor: colors.footerBg, 
        borderTop: `1px solid ${colors.border}`,
        gap: 2
      }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          variant="outlined"
          startIcon={<CloseIcon />}
          sx={{ 
            borderRadius: 2, 
            px: 3,
            borderColor: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.23)',
            color: isDark ? '#b0b0b0' : '#666666',
            '&:hover': {
              borderColor: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.4)',
              bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)'
            }
          }}
        >
          {isRTL ? 'إلغاء' : 'Cancel'}
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || period?.isClosed}
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          sx={{ 
            borderRadius: 2,
            px: 4,
            background: colors.headerBg,
            boxShadow: isDark 
              ? '0 4px 14px rgba(30, 60, 114, 0.4)' 
              : '0 4px 14px rgba(102, 126, 234, 0.4)',
            '&:hover': { 
              background: isDark 
                ? 'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)'
                : 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
              boxShadow: isDark 
                ? '0 6px 20px rgba(30, 60, 114, 0.5)' 
                : '0 6px 20px rgba(102, 126, 234, 0.5)'
            }
          }}
        >
          {loading
            ? (isRTL ? 'جاري الحفظ...' : 'Saving...')
            : (isRTL ? 'حفظ الفترة' : 'Save Period')
          }
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FiscalPeriodDialog;